import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { aiService } from '../api/ai.service';
import type { ApiTaskPlanApplyRequest } from '../api/ai.contract';
import type { ApplyTaskPlanResult, TaskPlanProposal } from '../types';
import type { TaskPlanDraftFormValues } from '../schemas';

export interface ApplyTaskPlanVariables {
  drafts: TaskPlanDraftFormValues[];
  proposal: TaskPlanProposal;
}

/**
 * Apply mutation for the AI goal-to-task flow.
 * Invalidation on settle: the tasks root always.
 * No optimistic insert (D7). orgId comes from the store.
 */
export function useApplyTaskPlan(projectId: string, boardId: string) {
  const orgId = useOrganizationStore((s) => s.activeOrganizationId) ?? '';
  const queryClient = useQueryClient();

  return useMutation<ApplyTaskPlanResult, unknown, ApplyTaskPlanVariables>({
    mutationFn: ({ drafts, proposal }) => {
      const selectedTasks = drafts
        .filter((d) => d.selected)
        .map((d) => ({
          title: d.title,
          description: null,
          priority: d.priority ?? null,
          estimatedMinutes: d.estimatedMinutes ?? null,
        }));

      const request: ApiTaskPlanApplyRequest = {
        correlationId: proposal.correlationId,
        tasks: selectedTasks,
      };

      return aiService.applyTaskPlan(projectId, boardId, request);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks', orgId, projectId] });
    },
  });
}
